// src/utils/statistics.js

import { extractStatus } from './processors';
import { SCAN_PATTERNS } from '../constants';
import { hasMeaningfulValue, getStatusColor, pct } from './helpers';

export const computeStats = (data) => {
  const total = data.length;
  
  const scanned = data.filter(row =>
    ['Condition', 'PSU-1 Cond', 'PSU-2 Cond'].some(col =>
      SCAN_PATTERNS.userPrefix.test(row[col] || '')
    )
  ).length;
  
  const condGood = data.filter(row => extractStatus(row['Condition']) === 'good').length;
  const condDmg = data.filter(row => extractStatus(row['Condition']) === 'damaged').length;
  const condEmpty = total - condGood - condDmg;
  
  const psu1Good = data.filter(row => extractStatus(row['PSU-1 Cond']) === 'good').length;
  const psu1Dmg = data.filter(row => extractStatus(row['PSU-1 Cond']) === 'damaged').length;
  const psu1Empty = total - psu1Good - psu1Dmg;
  
  const psu2Good = data.filter(row => extractStatus(row['PSU-2 Cond']) === 'good').length;
  const psu2Dmg = data.filter(row => extractStatus(row['PSU-2 Cond']) === 'damaged').length;
  const psu2Empty = total - psu2Good - psu2Dmg;
  
  const powerCord = data.filter(row => (row['Power Cord'] || '').trim().toLowerCase() === 'ok').length;
  const rails = data.filter(row => (row['Rails'] || '').trim().toLowerCase() === 'ok').length;
  
  return {
    total,
    scanned,
    notScanned: total - scanned,
    condGood,
    condDmg,
    condEmpty,
    psu1Good,
    psu1Dmg,
    psu1Empty,
    psu2Good,
    psu2Dmg,
    psu2Empty,
    powerCord,
    rails,
  };
};

export const computeInventoryAnalytics = (data, columnMap) => {
  if (!data?.length) {
    return {
      total: 0,
      unique: 0,
      scanned: 0,
      damaged: 0,
      good: 0,
      pending: 0,
      scanRate: 0,
      conditionScore: 0,
      products: [],
    };
  }
  
  const productMap = new Map();
  let scanned = 0, damaged = 0, good = 0, pending = 0;
  
  data.forEach(row => {
    const productId = row[columnMap.productId] || 'Unknown';
    const hasScan = hasMeaningfulValue(row[columnMap.serialNo]);
    const statusColor = getStatusColor(row[columnMap.condition]);
    const isDamaged = statusColor === 'danger';
    const isGood = statusColor === 'success';
    
    if (hasScan) scanned++;
    if (isDamaged) damaged++;
    if (isGood) good++;
    if (!hasScan && !isDamaged && !isGood) pending++;
    
    if (!productMap.has(productId)) {
      productMap.set(productId, {
        productId,
        total: 0,
        scanned: 0,
        damaged: 0,
        good: 0,
      });
    }
    
    const product = productMap.get(productId);
    product.total++;
    if (hasScan) product.scanned++;
    if (isDamaged) product.damaged++;
    if (isGood) product.good++;
  });
  
  const total = data.length;
  
  return {
    total,
    unique: productMap.size,
    scanned,
    damaged,
    good,
    pending,
    scanRate: pct(scanned, total),
    conditionScore: pct(total - damaged, total),
    products: Array.from(productMap.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 6),
  };
};

// Re-export commonly used functions from other modules for convenience
export { buildTechActivity, getRowDate, extractStatus, parseStrictScanLines, extractDatesFromScanCell } from './processors';
export { pct, shortText, normaliseDate, hasMeaningfulValue, getStatusColor, isPassValue, isFailValue } from './helpers';